/**
 * One field of the generator brief.
 *
 * Most fields are a select over a fixed list. Brand identity is not: a campaign
 * title is typed, a logo is uploaded, a palette is a handful of colours, and
 * contact details are a small structured group. `section.kind` says which,
 * defaulting to a select so every existing field keeps its exact behaviour.
 *
 * A field with `enabled: false` still renders — disabled, with a lock — so the
 * layout never shifts as phases unlock.
 */
import React, { useRef, useState } from 'react';
import { Lock, Upload, X, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Contact rows the brief carries. Kept small — this is a design brief, not a CRM. */
export const CONTACT_FIELDS = [
  { id: 'phone', label: 'Phone', placeholder: '(555) 014-2233' },
  { id: 'email', label: 'Email', placeholder: 'hello@example.com' },
  { id: 'website', label: 'Website', placeholder: 'example.com' },
  { id: 'address', label: 'Address', placeholder: '125 Willow Avenue' },
];

/** Palette slots. Three is enough to express a brand without becoming a theme editor. */
export const BRAND_COLOR_SLOTS = ['Primary', 'Secondary', 'Accent'];

const labelClass = 'text-xs font-medium text-gray-600 mb-1 flex items-center gap-1.5';
const inputClass =
  'w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-800 ' +
  'focus:outline-none focus:ring-2 focus:ring-purple-500';

/**
 * Uploads through the admin asset endpoint the template screens already use.
 * No second upload system, and no image bytes in the wizard's own state.
 */
const uploadAsset = async (file) => {
  const form = new FormData();
  form.append('file', file);
  let token = '';
  try { token = localStorage.getItem('admin_token') || ''; } catch { /* storage unavailable */ }

  const res = await fetch(`${API_URL}/api/admin/content/assets/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) throw new Error('Upload failed');
  const asset = await res.json();
  const url = asset.url || '';
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

const LogoField = ({ value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadAsset(file));
    } catch (err) {
      toast({ title: 'Logo upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (value) {
    return (
      <div className="flex items-center gap-2">
        <img src={value} alt="Brand logo" className="h-9 w-9 rounded border border-gray-200 object-contain bg-white" />
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Remove logo"
          className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
        >
          <X className="w-3 h-3" /> Remove
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className={`${inputClass} flex items-center gap-2 text-gray-500 hover:border-purple-300 disabled:opacity-60`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {busy ? 'Uploading…' : 'Upload logo'}
      </button>
    </>
  );
};

const ColorsField = ({ value, onChange }) => {
  const colors = value || {};
  return (
    <div className="flex gap-2">
      {BRAND_COLOR_SLOTS.map((slot) => (
        <div key={slot} className="flex-1">
          <input
            type="color"
            aria-label={`${slot} brand color`}
            value={colors[slot] || '#ffffff'}
            onChange={(e) => onChange({ ...colors, [slot]: e.target.value })}
            className="h-9 w-full rounded-md border border-gray-200 bg-white p-1 cursor-pointer"
          />
          <span className="text-[10px] text-gray-400">{slot}</span>
        </div>
      ))}
    </div>
  );
};

const ContactField = ({ value, onChange }) => {
  const contact = value || {};
  return (
    <div className="grid grid-cols-2 gap-2">
      {CONTACT_FIELDS.map((f) => (
        <input
          key={f.id}
          type="text"
          aria-label={f.label}
          placeholder={f.placeholder}
          value={contact[f.id] || ''}
          onChange={(e) => onChange({ ...contact, [f.id]: e.target.value })}
          className={inputClass}
        />
      ))}
    </div>
  );
};

const SectionField = ({ section, value, onChange, options = [] }) => {
  const { label, enabled, placeholder, phase, kind = 'select' } = section;

  const control = () => {
    if (!enabled) {
      return (
        <select
          disabled
          aria-label={label}
          className="w-full h-9 rounded-md border border-gray-100 bg-gray-50 px-2 text-sm text-gray-400 cursor-not-allowed"
        >
          <option>{placeholder || `Available in Phase ${phase}`}</option>
        </select>
      );
    }
    if (kind === 'text') {
      return (
        <input
          type="text"
          aria-label={label}
          placeholder={placeholder || ''}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      );
    }
    if (kind === 'upload') return <LogoField value={value} onChange={onChange} />;
    if (kind === 'colors') return <ColorsField value={value} onChange={onChange} />;
    if (kind === 'contact') return <ContactField value={value} onChange={onChange} />;

    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  };

  return (
    <label className="block">
      <span className={labelClass}>
        {label}
        {!enabled && <Lock className="w-3 h-3 text-gray-300" aria-label="Locked" />}
      </span>
      {control()}
    </label>
  );
};

export default SectionField;
